import React from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  faCog,
  faUser,
  faCrown,
  faExclamationTriangle,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import axiosUserInstance from "../../axiosUserInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";

const SettingsTab = ({ userData, setUserData, setActiveSection }) => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  const deleteAccount = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteUser();
      }
    });
  };

  const handleDeleteUser = async () => {
    try {
      const response = await axiosUserInstance.delete(`/user/delete`);
      if (response?.status === 200) {
        setUserData((u) => ({ ...u, name: "Deleted User" }));
        localStorage.removeItem("token");
        localStorage.removeItem("userInfo");
        toast.success("User deleted successfully.");
        navigate("/auth/login");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast.error(apiError);
    }
  };

  const updateUserData = async (payload) => {
    try {
      setLoading(true);
      const response = await axiosUserInstance.put(`/user/update`, payload);
      if (response?.status === 200) {
        const data = await response.data?.data;
        setUserData((prev) => ({
          ...prev,
          fullName: data.name || prev.fullName,
          status: data.status,
          nickname: `@${data.userName}`,
          bio: data.bio || prev.bio,
          bioScore: data.bioScore || 0,
          bannerImage: data?.bannerImage || prev.bannerImage,
          profilePicture: data.profileImage || prev.profilePicture,
          socialLinks:
            data.socialLinks?.length > 0 ? data.socialLinks : prev.socialLinks,
          tags: data.tags?.length > 0 ? data.tags : [],
          albums: data.albums?.length > 0 ? data.albums : [],
          isProfileLinkDisable: data?.isProfileLinkDisable || false,
        }));
        toast.success("User updated successfully.");
      } else {
        toast.error(
          response?.message || "User update failed. Please try again."
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="section-title">
        <FontAwesomeIcon icon={faCog} /> Settings
      </h2>

      <div className="settings-card">
        <h3 className="settings-title">
          <FontAwesomeIcon icon={faCrown} /> Account Tier Status
        </h3>
        <div className="tier-info">
          <span
            className={`tier-badge text-capitalize ${
              userData.plan === "Free" ? "plan-free" : "plan-premium"
            }`}
          >
            {/* {userData.plan === "free" ? "Free Plan" : "Premium Plan"} */}
            {userData?.plan} Plan
          </span>
          {userData.plan !== "yearly" && <p>
            Upgrade to unlock premium features and increase your storage limit.
          </p>}
        </div>
        {userData.plan !== "yearly" && <button
          className="plan-button btn-primary"
          onClick={() => setActiveSection("shop")}
        >
          Upgrade Plan
        </button>}
      </div>

      <div className="settings-card">
        <h3 className="settings-title">
          <FontAwesomeIcon icon={faUser} /> Profile Status
        </h3>
        <p>Change how others see your availability on BioME</p>
        <div className="status-options">
          {["active", "sleeping", "busy"].map((s) => (
            <div
              key={s}
              className={`status-option ${
                userData?.status === s ? "active" : ""
              }`}
              onClick={() => updateUserData({ status: s })}
            >
              <span className={`status-option-dot status-${s}`}></span>
              <span style={{ textTransform: "capitalize" }}>{s}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-card">
        <h3 className="settings-title">
          <FontAwesomeIcon icon={faLink} /> Profile Link Status
        </h3>
        <div
          className="toggle-label"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Temporarily disable your profile link</span>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={userData?.isProfileLinkDisable}
              onChange={(e) => {
                Swal.fire({
                  title: "Are you sure?",
                  text: "Your profile link will be temporarily disabled!",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonColor: "#d33",
                  cancelButtonColor: "#3085d6",
                  confirmButtonText: "Yes, delete it!",
                }).then((result) => {
                  if (result.isConfirmed) {
                    updateUserData({
                      isProfileLinkDisable: !userData?.isProfileLinkDisable,
                    });
                  }
                });
              }}
            />
            <span className="toggle-slider" />
          </label>
        </div>
        <p className="danger-text">
          When disabled, your profile will not be accessible to anyone.
        </p>
      </div>

      <div className="danger-zone">
        <h3 className="danger-title">
          <FontAwesomeIcon icon={faExclamationTriangle} /> Terminate Account
        </h3>
        <p className="danger-text">
          Deleting your account will permanently remove all your data including
          profile, albums, and settings. This action cannot be undone.
        </p>
        <button className="danger-button" onClick={deleteAccount}>
          Delete Account
        </button>
      </div>
    </div>
  );
};

export default SettingsTab;
